import { Controller } from "@hotwired/stimulus"
import QRCode from "qrcode"

export default class extends Controller {
  static targets = ["firstModal", "detailModal", "qrModal"]
  static values = {
    url: String,
    shareUrl: String,
    qrUrl: String,
    completeUrl: String
  }

  connect() {
    this.shareLink = null
  }

  async onQrModalOpen(event) {
    // Wait for modal animation to complete (modal takes ~150ms to open)
    setTimeout(async () => {
      await this.generateQrCodeFromBackend()
    }, 300)
  }

  async switchToDetail(event) {
    event.preventDefault()
    event.stopPropagation()

    // If we have a completeUrl, call the backend to complete the conversation
    if (this.hasCompleteUrlValue) {
      const success = await this.completeConversationWithRetry()
      if (!success) {
        // Error toast is shown inside completeConversationWithRetry
        return
      }
    }

    // Fetch share link from backend if we have shareUrlValue
    if (this.hasShareUrlValue && !this.shareLink) {
      await this.fetchShareLink()
    }

    // Find modal elements
    const firstModalElement = this.firstModalTarget.querySelector('[data-controller="modal"]')
    const detailModalElement = this.detailModalTarget.querySelector('[data-controller="modal"]')
    const feedback = this.detailModalTarget.querySelector('[data-controller="conversation-feedback"]')

    // Close first modal
    if (firstModalElement) {
      const firstModalController = this.application.getControllerForElementAndIdentifier(
        firstModalElement,
        "modal"
      )

      if (firstModalController) {
        firstModalController.close()
      }
    }

    // Wait for animation to complete, then open detail modal
    setTimeout(() => {
      if (detailModalElement) {
        const detailModalController = this.application.getControllerForElementAndIdentifier(
          detailModalElement,
          "modal"
        )

        if (detailModalController) {
          detailModalController.open()
          feedback?.dispatchEvent(new CustomEvent("conversation-feedback:activate", { detail: { url: this.feedbackUrl, question: this.feedbackQuestion } }))
        }
      }
    }, 200)
  }

  async completeConversationWithRetry(maxRetries = 3) {
    let lastError = null

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(this.completeUrlValue, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Accept': 'application/json'
          }
        })

        if (response.ok) {
          const data = await response.json()
          this.feedbackUrl = data.feedback_url
          this.feedbackQuestion = data.feedback_question
          console.log('Conversation completed:', data)
          this.disableCompletionButtons()
          return true
        }

        // Handle specific HTTP errors
        const errorMessage = await this.getErrorMessageFromResponse(response)
        lastError = new Error(`HTTP ${response.status}: ${errorMessage}`)

        // Don't retry on 4xx errors (client errors) except 429 (rate limit)
        if (response.status >= 400 && response.status < 500 && response.status !== 429) {
          break
        }

        // Wait before retry (exponential backoff)
        if (attempt < maxRetries) {
          await this.delay(1000 * Math.pow(2, attempt - 1))
        }
      } catch (error) {
        lastError = error
        console.error(`Completion attempt ${attempt} failed:`, error)

        // Wait before retry
        if (attempt < maxRetries) {
          await this.delay(1000 * Math.pow(2, attempt - 1))
        }
      }
    }

    // Show error toast to user
    this.showErrorToast(lastError)
    return false
  }

  disableCompletionButtons() {
    document.querySelectorAll("[data-complete-conversation-button]").forEach((button) => {
      button.disabled = true
    })
  }

  async getErrorMessageFromResponse(response) {
    try {
      const data = await response.json()
      return data.error || `Request failed with status ${response.status}`
    } catch {
      return `Request failed with status ${response.status}`
    }
  }

  showErrorToast(error) {
    const message = error?.message?.includes('403')
      ? 'Unable to complete session. Please refresh the page and try again.'
      : error?.message?.includes('500')
      ? 'Server error. Our team has been notified. Please try again later.'
      : 'Failed to complete session. Please check your connection and try again.'

    if (window.toastController) {
      window.toastController.error(message)
    } else {
      // Fallback to native alert if toast controller not available
      alert(message)
    }
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  // Legacy method - kept for compatibility but not used in main flow
  async completeConversation() {
    return this.completeConversationWithRetry(1)
  }

  async fetchShareLink() {
    try {
      const response = await fetch(this.shareUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.shareLink = data.url
        // Update urlValue for QR code generation
        this.urlValue = data.url
      }
    } catch (error) {
      console.error('Error fetching share link:', error)
    }
  }

  async switchToQrCode(event) {
    event.preventDefault()
    event.stopPropagation()

    // Fetch share link if not already fetched
    if (this.hasShareUrlValue && !this.shareLink) {
      await this.fetchShareLink()
    }

    // Generate QR code before switching
    await this.generateQrCodeFromBackend()

    // Find detail modal and QR modal elements
    const detailModalElement = this.detailModalTarget.querySelector('[data-controller="modal"]')
    const qrModalElement = this.qrModalTarget.querySelector('[data-controller="modal"]')

    // Close detail modal
    if (detailModalElement) {
      const detailModalController = this.application.getControllerForElementAndIdentifier(
        detailModalElement,
        "modal"
      )

      if (detailModalController) {
        detailModalController.close()
      }
    }

    // Wait for animation to complete, then open QR modal
    setTimeout(() => {
      if (qrModalElement) {
        const qrModalController = this.application.getControllerForElementAndIdentifier(
          qrModalElement,
          "modal"
        )

        if (qrModalController) {
          qrModalController.open()
        }
      }
    }, 200)
  }

  async generateQrCodeFromBackend() {
    // If we have qrUrlValue, fetch QR code from backend
    if (this.hasQrUrlValue) {
      try {
        const response = await fetch(this.qrUrlValue, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
            'Accept': 'application/json'
          }
        })

        if (response.ok) {
          const data = await response.json()

          // Store share URL from response
          if (data.url && !this.shareLink) {
            this.shareLink = data.url
            this.urlValue = data.url
          }

          const qrContainer = this.qrModalTarget.querySelector('.qr-code-container')

          if (qrContainer) {
            if (data.svg) {
              // Store SVG for download
              this.qrSvgData = data.svg
              qrContainer.innerHTML = data.svg
            } else if (data.qr_api_url) {
              this.qrImageUrl = data.qr_api_url
              qrContainer.innerHTML = `<img src="${data.qr_api_url}" alt="QR Code" class="w-full h-full" />`
            }
          }
          return
        }
      } catch (error) {
        console.error('Error fetching QR code from backend:', error)
      }
    }

    // Fallback to client-side generation
    this.generateQrCode()
  }

  generateQrCode() {
    // Get the URL to encode (you can pass this as a data attribute)
    const url = this.urlValue || window.location.href

    // Find the QR code container in the QR modal
    const qrContainer = this.qrModalTarget.querySelector('.qr-code-container')

    if (!qrContainer) return

    // Clear existing content
    qrContainer.innerHTML = ''

    // Create canvas element
    const canvas = document.createElement('canvas')
    qrContainer.appendChild(canvas)

    // Generate QR code
    QRCode.toCanvas(canvas, url, {
      width: 200,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    }, (error) => {
      if (error) {
        console.error('QR Code generation error:', error)
      }
    })
  }

  downloadQrCode(event) {
    event.preventDefault()

    // Try to download SVG if we have it (from backend)
    if (this.qrSvgData) {
      this.downloadSvgAsImage(this.qrSvgData)
      return
    }

    // Try to download from external image URL
    if (this.qrImageUrl) {
      const link = document.createElement('a')
      link.download = 'qr-code.png'
      link.href = this.qrImageUrl
      link.click()
      return
    }

    // Fallback to canvas download (client-side generated)
    const canvas = this.qrModalTarget.querySelector('.qr-code-container canvas')
    if (!canvas) return

    // Convert canvas to blob
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.download = 'qr-code.png'
      link.href = url
      link.click()
      URL.revokeObjectURL(url)
    })
  }

  downloadSvgAsImage(svgString) {
    // Create a canvas to convert SVG to PNG
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()

    // Set canvas size (adjust as needed)
    canvas.width = 400
    canvas.height = 400

    img.onload = () => {
      // Fill white background
      ctx.fillStyle = '#FFFFFF'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Draw the SVG image
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

      // Convert to PNG and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.download = 'qr-code.png'
        link.href = url
        link.click()
        URL.revokeObjectURL(url)
      })
    }

    // Create blob URL from SVG string
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const svgUrl = URL.createObjectURL(svgBlob)
    img.src = svgUrl
  }

  async copyLink(event) {
    event.preventDefault()

    // Save button reference BEFORE any async operations
    const button = event.currentTarget

    // Fetch share link if not already fetched
    if (this.hasShareUrlValue && !this.shareLink) {
      await this.fetchShareLink()
    }

    const url = this.shareLink || this.urlValue || window.location.href

    navigator.clipboard.writeText(url)
      .then(() => {
        // Show success feedback - change icon and text
        if (!button) return

        const icon = button.querySelector('svg')

        // Find text node - it's a direct child text node of the button
        let textNode = null
        for (let node of button.childNodes) {
          if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
            textNode = node
            break
          }
        }

        // Store originals
        const originalIconHTML = icon ? icon.outerHTML : ''
        const originalText = textNode ? textNode.textContent.trim() : 'Copy Link'

        // Replace with check icon
        if (icon) {
          icon.outerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`
        }

        // Change text to "Copied"
        if (textNode) {
          textNode.textContent = 'Copied'
        }

        // Restore after 2 seconds
        setTimeout(() => {
          const currentIcon = button.querySelector('svg')
          if (currentIcon && originalIconHTML) {
            currentIcon.outerHTML = originalIconHTML
          }
          if (textNode) {
            textNode.textContent = originalText
          }
        }, 2000)
      })
      .catch(err => {
        console.error('Failed to copy:', err)
      })
  }
}
